import { useState } from "react";
import { toast } from "sonner";
import { formatRelativeShort } from "@/design/format";
import { cn } from "@/design/cn";
import {
  Stat,
  Sparkline,
  FilterChips,
  Pill,
  Button,
  EmptyState,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  HeimdallAttribution,
} from "@/design/components";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSeed, useMoney, useAnalystName, useProgramme } from "@/domain/hooks";
import { useLeakActions } from "@/domain/useMutations";
import type { Leak, LeakCategory } from "@/domain/types";

const CATEGORY_LABEL: Record<LeakCategory, string> = {
  timing_variance: "Timing variance",
  anomalous_supplier: "Anomalous supplier",
  missing_evidence: "Missing evidence",
  threshold_split: "Threshold split",
  suspected_duplicate: "Suspected duplicate",
};

// Severity follows the reconciliation rule book: splits and duplicates are serious,
// supplier and evidence gaps need attention, timing variances are a note.
type Severity = "serious" | "attention" | "info";
const CATEGORY_SEVERITY: Record<LeakCategory, Severity> = {
  threshold_split: "serious",
  suspected_duplicate: "serious",
  anomalous_supplier: "attention",
  missing_evidence: "attention",
  timing_variance: "info",
};
const SEVERITY_TONE: Record<Severity, "danger" | "warn" | "info"> = { serious: "danger", attention: "warn", info: "info" };
const SEVERITY_LABEL: Record<Severity, string> = { serious: "Serious", attention: "Attention", info: "Note" };
const SEVERITY_BORDER: Record<Severity, string> = { serious: "border-l-bad", attention: "border-l-warn", info: "border-l-info" };

export function LeaksInbox() {
  const seed = useSeed();
  const money = useMoney();
  const { setState: setLeakSt, isPending: acting } = useLeakActions();
  const analystName = useAnalystName();
  const programme = useProgramme();
  const [filter, setFilter] = useState("open");
  const [openId, setOpenId] = useState<string | null>(null);
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const [drawerNote, setDrawerNote] = useState("");

  const filters = [
    { value: "open", label: "Open" },
    { value: "investigating", label: "Under investigation" },
    { value: "resolved", label: "Resolved" },
    { value: "all", label: "All" },
  ];

  const rows = seed.leaks.filter((l) => (filter === "all" ? true : l.state === filter));
  const open = seed.leaks.filter((l) => l.state === "open" || l.state === "investigating");
  const totalFlagged = seed.leaks.reduce((s, l) => s + (l.amount ?? 0), 0);
  const flaggedPct = programme.totalBudget > 0 ? ((totalFlagged / programme.totalBudget) * 100).toFixed(1) : "0";

  // Average age of open leaks, in human units (e.g. "9d").
  const now = Date.now();
  const avgAgeDays = open.length
    ? Math.round(open.reduce((s, l) => s + (now - new Date(l.detectedAt).getTime()), 0) / open.length / 86_400_000)
    : 0;

  async function action(l: Leak, state: Leak["state"], message: string) {
    await setLeakSt(l.id, state);
    toast.message(message);
    setOpenId(null);
  }

  const openLeak = seed.leaks.find((l) => l.id === openId) ?? null;

  return (
    <div className="space-y-5">
      {/* TODO(heimdall): AI analyst not built yet — re-enable the Heimdall attribution and
          findings-queue link when Heimdall ships.
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-x-1 text-xs text-mute">
          <HeimdallAttribution name={analystName} firstMention /> surfaces these from the reconciliation feed.
        </span>
        <Link to="/stratus/signals/heimdall" className="text-xs font-medium text-brand hover:underline">Open findings queue</Link>
      </div>
      */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Open leaks" value={String(open.length)} subTone={open.length ? "warn" : "ok"} sub={open.length ? "need a decision" : "all clear"} trend={<Sparkline data={[2, 3, 3, 4, 4, open.length]} />} />
        <Stat label="Average age" value={`${avgAgeDays}d`} sub="of open leaks" subTone={avgAgeDays >= 10 ? "bad" : avgAgeDays >= 5 ? "warn" : "ok"} trend={<Sparkline data={[3, 4, 5, 6, 7, avgAgeDays]} />} />
        <Stat label="Resolved this week" value="1" sub="closed with a note" />
        <Stat label="Value flagged" value={money(totalFlagged)} sub={`${flaggedPct}% of programme budget`} subTone={parseFloat(flaggedPct) > 5 ? "bad" : parseFloat(flaggedPct) > 1 ? "warn" : "ok"} />
      </div>

      <FilterChips options={filters} value={filter} onChange={setFilter} />

      {rows.length === 0 ? (
        <EmptyState title="No open leaks at the moment" body="The reconciliation engine surfaces leaks the moment a discrepancy crosses a configured rule." />
      ) : (
        <div className="space-y-3">
          {rows.map((l) => (
            <div key={l.id} className={cn("rounded-card border border-line bg-card p-4 border-l-4", SEVERITY_BORDER[CATEGORY_SEVERITY[l.category]])}>
              <div className="flex items-center gap-2">
                <Pill variant={SEVERITY_TONE[CATEGORY_SEVERITY[l.category]]} size="sm" className="uppercase tracking-wide">{SEVERITY_LABEL[CATEGORY_SEVERITY[l.category]]}</Pill>
                <span className="text-meta text-faint">{CATEGORY_LABEL[l.category]}</span>
                <span className="data text-xs text-faint">{formatRelativeShort(l.detectedAt)}</span>
                {l.state === "investigating" && <Pill variant="info" size="sm">Under investigation</Pill>}
                {l.state === "resolved" && <Pill variant="success" size="sm">Resolved</Pill>}
                <span className="data ml-auto text-xs text-faint">{l.id}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-ink">{l.title}</p>
              <p className="mt-1 text-sm text-mute">{l.body}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-faint">
                {l.amount && <span className="num tabular-nums">{money(l.amount)}</span>}
                <span>{seed.projects.find((p) => p.id === l.projectId)?.name}</span>
              </div>
              {(l.state === "open" || l.state === "investigating") && (
                <>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setOpenId(l.id)}>Open</Button>
                    {l.state === "open" && <Button size="sm" variant="secondary" disabled={acting} onClick={() => action(l, "investigating", "Marked under investigation")}>Mark investigating</Button>}
                    {resolveId !== l.id && (
                      <Button size="sm" variant="secondary" disabled={acting} onClick={() => { setResolveId(l.id); setResolveNote(""); }}>Resolve with note</Button>
                    )}
                    <Button size="sm" variant="ghost" disabled={acting} onClick={() => action(l, "not_a_leak", "Marked as not a leak")}>Not a leak</Button>
                  </div>
                  {resolveId === l.id && (
                    <div className="mt-3 flex flex-col gap-2">
                      <textarea
                        autoFocus
                        rows={2}
                        value={resolveNote}
                        onChange={(e) => setResolveNote(e.target.value)}
                        placeholder="Describe how this was resolved…"
                        className="w-full resize-none rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={!resolveNote.trim()}
                          onClick={() => { action(l, "resolved", `Resolved: ${resolveNote.trim()}`); setResolveId(null); setResolveNote(""); }}
                        >
                          Confirm resolve
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setResolveId(null); setResolveNote(""); }}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <Drawer open={!!openLeak} onOpenChange={(o) => { if (!o) { setOpenId(null); setDrawerNote(""); } }} width="560px">
        {openLeak && (
          <>
            <DrawerHeader>
              <div className="flex items-center gap-2">
                <Pill variant={SEVERITY_TONE[CATEGORY_SEVERITY[openLeak.category]]} size="sm" className="uppercase tracking-wide">{SEVERITY_LABEL[CATEGORY_SEVERITY[openLeak.category]]}</Pill>
                <span className="text-meta text-faint">{CATEGORY_LABEL[openLeak.category]}</span>
              </div>
              <DrawerTitle className="mt-2 text-base font-semibold text-ink">{openLeak.title}</DrawerTitle>
            </DrawerHeader>
            <DrawerBody className="space-y-4">
              <p className="text-sm text-mute">{openLeak.body}</p>
              <div>
                <h4 className="mb-1 text-meta text-faint">Rule triggered</h4>
                <p className="rounded-control bg-subtle px-3 py-2 text-sm text-mute">{openLeak.triggeredRule}</p>
              </div>
              <div>
                <h4 className="mb-1 text-meta text-faint">Contributing records</h4>
                <ul className="space-y-1">
                  {openLeak.contributingItemIds.map((id) => (
                    <li key={id}>
                      <Link
                        to="/stratus/approvals"
                        className="data flex items-center justify-between gap-2 rounded-control border border-line bg-card px-3 py-2 text-xs text-mute transition-colors hover:border-brand hover:text-ink"
                      >
                        <span className="font-mono">{id}</span>
                        <ArrowUpRight className="h-3 w-3 shrink-0 text-faint" aria-hidden />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              {(openLeak.state === "open" || openLeak.state === "investigating") && (
                <div>
                  <h4 className="mb-1 text-meta text-faint">Resolution note</h4>
                  <textarea
                    rows={3}
                    value={drawerNote}
                    onChange={(e) => setDrawerNote(e.target.value)}
                    placeholder="Describe how this was resolved…"
                    className="w-full resize-none rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                </div>
              )}
            </DrawerBody>
            <DrawerFooter>
              {openLeak.state === "open" && <Button variant="secondary" onClick={() => action(openLeak, "investigating", "Marked under investigation")}>Mark investigating</Button>}
              {(openLeak.state === "open" || openLeak.state === "investigating") && (
                <Button disabled={!drawerNote.trim()} onClick={() => { action(openLeak, "resolved", `Resolved: ${drawerNote.trim()}`); setDrawerNote(""); }}>Resolve with note</Button>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </div>
  );
}
